import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Bold,
  Copy,
  ExternalLink,
  Heading2,
  Heading3,
  Image as ImageIcon,
  Italic,
  Link2,
  List,
  ListOrdered,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { ApiClientError } from "@/api/client";
import {
  createKbDraft,
  fetchKbItem,
  fetchKbSection,
  saveKbDraft,
  uploadKbContentImage,
} from "@/api/knowledge";
import { KbImage, registerKbImagePreview, releaseKbImagePreviews } from "./kbImageNode";
import { KbOwnerPicker } from "./KbOwnerPicker";
import { KbErrorCard, KbNotFound } from "./kbUi";

/**
 * D-147 W1 S4 — редактор чернетки (visual package v1.2 LOCK, екран ④).
 *
 * Інваріанти: FE шле ЛИШЕ contentJson (жодного client-HTML — 422-гейт S1) ·
 * publish із редактора НЕ існує (єдиний шлях = картка-чернетка; owner має
 * «Зберегти й перейти до перевірки») · optimistic concurrency: save несе
 * expectedDraftRevision, розбіжність → 409 draft_conflict-банер, КОНТЕНТ
 * ЛИШАЄТЬСЯ (дії: актуальна в новій вкладці / скопіювати мій текст) ·
 * закриття з незбереженим = 3-way · type=file → лише метадані (назва/папка).
 *
 * Схема TipTap = алфавіт рендерера §7 (heading 2/3, b/i, списки, link,
 * image-нода з attachmentId) — решта StarterKit-нод вимкнена, паста зайвого
 * ріжеться на рівні схеми.
 */
export function KnowledgeEditorPage() {
  const { itemId } = useParams<{ itemId: string }>();
  const id = itemId ?? "";
  const navigate = useNavigate();
  const qc = useQueryClient();

  const item = useQuery({
    queryKey: ["kbItem", id],
    queryFn: ({ signal }) => fetchKbItem(id, signal),
    enabled: !!id,
    staleTime: 0,
    retry: (count, e) => !(e instanceof ApiClientError && e.status === 404) && count < 2,
  });

  const section = useQuery({
    queryKey: ["kbSection", item.data?.sectionId ?? ""],
    queryFn: ({ signal }) => fetchKbSection(item.data?.sectionId ?? "", signal),
    enabled: !!item.data?.sectionId,
    staleTime: 30_000,
  });

  const [title, setTitle] = useState("");
  const [titleError, setTitleError] = useState(false);
  const [folderId, setFolderId] = useState<string>("");
  const [rev, setRev] = useState<number | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{ author: string; at: string } | null>(null);
  const [closeAsk, setCloseAsk] = useState(false);
  const [copied, setCopied] = useState(false);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const initedRef = useRef(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const isPage = item.data?.type === "page";

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        blockquote: false,
        codeBlock: false,
        code: false,
        horizontalRule: false,
        strike: false,
        underline: false,
        hardBreak: false,
        link: { openOnClick: false, autolink: true },
      }),
      KbImage.configure({ itemId: id }),
    ],
    content: null,
    onUpdate: () => setDirty(true),
    editorProps: {
      attributes: {
        class:
          "kb-editor min-h-[260px] px-4 py-3 text-sm leading-relaxed outline-none [&_a]:text-primary [&_a]:underline [&_h2]:mb-1.5 [&_h2]:mt-4 [&_h2]:text-lg [&_h2]:font-bold [&_h3]:mb-1.5 [&_h3]:mt-3 [&_h3]:text-[15px] [&_h3]:font-bold [&_li]:my-0.5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:my-1.5 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6",
      },
    },
  });

  // Ініціалізація форми з draft-снімка — РІВНО раз (щоб не клобати ввід)
  useEffect(() => {
    const draft = item.data?.draft;
    if (!draft || initedRef.current || !editor) return;
    initedRef.current = true;
    setTitle(draft.title);
    setFolderId(draft.folderId ?? "");
    setRev(draft.revision);
    setSavedAt(draft.modifiedAt || null);
    if (isPage && draft.contentJson) {
      editor.commands.setContent(draft.contentJson as never, { emitUpdate: false });
    }
    setDirty(false);
  }, [item.data?.draft, editor, isPage]);

  useEffect(() => () => releaseKbImagePreviews(), []);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["kbItem", id] });
    void qc.invalidateQueries({ queryKey: ["kbSectionItems"] });
    void qc.invalidateQueries({ queryKey: ["kbHome"] });
  };

  const doSave = async (): Promise<number | null> => {
    if (!item.data || rev === null) return null;
    const trimmed = title.trim();
    if (trimmed === "") {
      setTitleError(true);
      return null;
    }
    setTitleError(false);
    setSaving(true);
    setSaveError(null);
    setConflict(null);
    try {
      const resp = await saveKbDraft(id, {
        expectedDraftRevision: rev,
        draftTitle: trimmed,
        draftFolderId: folderId || null,
        ...(isPage && editor ? { contentJson: editor.getJSON() } : {}),
      });
      setRev(resp.draftRevision);
      setSavedAt(new Date().toISOString());
      setDirty(false);
      invalidate();
      return resp.draftRevision;
    } catch (e) {
      if (e instanceof ApiClientError && e.status === 409 && e.code === "draft_conflict") {
        // контент ЛИШАЄТЬСЯ; тягнемо свіжу мету чернетки для банера
        try {
          const fresh = await fetchKbItem(id);
          setConflict({
            author: fresh.draft?.modifiedBy?.name ?? "інший користувач",
            at: formatTime(fresh.draft?.modifiedAt ?? ""),
          });
        } catch {
          setConflict({ author: "інший користувач", at: "" });
        }
      } else {
        setSaveError(e instanceof Error ? e.message : "Не вдалося зберегти чернетку.");
      }
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveAndReview = async () => {
    const newRev = await doSave();
    if (newRev !== null) navigate(`/knowledge/items/${id}?view=draft`);
  };

  const close = () => {
    if (dirty) setCloseAsk(true);
    else navigate(`/knowledge/items/${id}`);
  };

  const onPickImage = (file: File | null) => {
    if (!file || !editor) return;
    setSaveError(null);
    uploadKbContentImage(id, file)
      .then(({ attachmentId }) => {
        // S1-P3a: до draftSave сервер denied-ить GET → local-blob превʼю
        registerKbImagePreview(attachmentId, URL.createObjectURL(file));
        editor
          .chain()
          .focus()
          .insertContent({ type: "image", attrs: { attachmentId, alt: file.name } })
          .run();
        setDirty(true);
      })
      .catch((e: unknown) =>
        setSaveError(e instanceof Error ? e.message : "Не вдалося завантажити зображення."),
      );
  };

  const setLink = () => {
    if (!editor) return;
    const prev = (editor.getAttributes("link").href as string) ?? "";
    const url = window.prompt("Посилання (https:// або mailto:)", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().unsetLink().run();
      return;
    }
    if (!/^(https:\/\/|mailto:)/i.test(url)) {
      setSaveError("Дозволені лише https:// та mailto: посилання.");
      return;
    }
    editor.chain().focus().setLink({ href: url }).run();
  };

  const notFound = item.error instanceof ApiClientError && item.error.status === 404;
  if (notFound) {
    return (
      <PageWrap>
        <KbNotFound text="Матеріал недоступний або не існує" />
      </PageWrap>
    );
  }
  if (item.isPending) {
    return (
      <PageWrap>
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
        </div>
      </PageWrap>
    );
  }
  if (item.isError || !item.data) {
    return (
      <PageWrap>
        <KbErrorCard text="Не вдалося завантажити матеріал" onRetry={() => void item.refetch()} />
      </PageWrap>
    );
  }

  const data = item.data;
  // Немає draft-ключів → reader (сервер не дав editor-payload) — редактор не для нього
  if (data.state === undefined) {
    return (
      <PageWrap>
        <KbNotFound text="Матеріал недоступний або не існує" />
      </PageWrap>
    );
  }

  if (data.state === "no_draft") {
    return (
      <PageWrap>
        <div className="rounded-2xl border border-dashed bg-card px-4 py-12 text-center">
          <div className="text-sm font-medium">Чернетки не існує</div>
          <div className="mt-1 text-sm text-muted-foreground">
            Створіть чернетку, щоб редагувати опубліковану версію.
          </div>
          {data.actions.createDraft && (
            <button
              type="button"
              disabled={creatingDraft}
              onClick={() => {
                setCreatingDraft(true);
                createKbDraft(id)
                  .then(() => {
                    initedRef.current = false;
                    invalidate();
                    void item.refetch();
                  })
                  .catch(() => setCreatingDraft(false))
                  .finally(() => setCreatingDraft(false));
              }}
              className="mt-3 rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              Створити чернетку
            </button>
          )}
        </div>
      </PageWrap>
    );
  }

  const ownerGrade =
    section.data?.capability === "owner" || section.data?.capability === "admin";
  const ownerName = section.data?.owner?.name ?? "";

  const tb = (
    label: string,
    icon: React.ReactNode,
    onClick: () => void,
    active: boolean,
  ) => (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "inline-flex h-8 w-8 items-center justify-center rounded-md text-sm hover:bg-muted",
        active && "bg-muted font-bold text-primary",
      )}
    >
      {icon}
    </button>
  );

  return (
    <PageWrap>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <nav className="mb-1 text-xs text-muted-foreground" aria-label="Хлібні крихти">
            <Link to={`/knowledge/sections/${data.sectionId}`} className="text-primary hover:underline">
              {data.sectionName}
            </Link>
            <span> / Редагування</span>
          </nav>
          <h1 className="flex flex-wrap items-center gap-2 text-[15px] font-semibold tracking-tight">
            {isPage ? "Редактор сторінки" : "Редактор файлу (метадані)"}
            <span className="inline-flex items-center rounded-full bg-warning px-2 py-0.5 text-[11px] font-semibold text-warning-foreground">
              чернетка · rev {rev}
              {savedAt ? ` · збережено ${formatTime(savedAt)}` : ""}
            </span>
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!ownerGrade && ownerName && (
            <span className="text-xs text-muted-foreground">
              Публікує власник розділу — {ownerName}
            </span>
          )}
          <button
            type="button"
            disabled={saving}
            onClick={() => void doSave()}
            className="rounded-md border px-3.5 py-2 text-sm font-semibold hover:bg-accent disabled:opacity-50"
          >
            {saving ? "Зберігаємо…" : "Зберегти чернетку"}
          </button>
          {ownerGrade && (
            <button
              type="button"
              disabled={saving}
              title="Збереже чернетку та відкриє картку у вкладці «Чернетка» для перевірки перед публікацією"
              onClick={() => void saveAndReview()}
              className="rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              Зберегти й перейти до перевірки
            </button>
          )}
          <button
            type="button"
            onClick={close}
            className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            Закрити
          </button>
        </div>
      </div>

      {conflict && (
        <div
          role="alert"
          className="mb-4 flex flex-wrap items-center gap-2 rounded-xl bg-warning px-3.5 py-2.5 text-sm text-warning-foreground"
        >
          <span className="min-w-0 flex-1">
            Чернетку оновлено: {conflict.author}
            {conflict.at ? `, ${conflict.at}` : ""}. Перегляньте актуальну версію — ваш текст
            лишився нижче.
          </span>
          <button
            type="button"
            onClick={() => window.open(`/knowledge/items/${id}?view=draft`, "_blank")}
            className="inline-flex items-center gap-1 font-semibold underline"
          >
            <ExternalLink className="h-3.5 w-3.5" aria-hidden /> Відкрити актуальну в новій вкладці
          </button>
          <button
            type="button"
            onClick={() => {
              const text = isPage && editor ? editor.getText() : title;
              void navigator.clipboard.writeText(text).then(() => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 2500);
              });
            }}
            className="inline-flex items-center gap-1 font-semibold underline"
          >
            <Copy className="h-3.5 w-3.5" aria-hidden />
            {copied ? "Скопійовано" : "Скопіювати мій текст"}
          </button>
        </div>
      )}
      {saveError && (
        <div role="alert" className="mb-4 rounded-xl bg-destructive-soft px-3.5 py-2.5 text-sm text-destructive-soft-foreground">
          {saveError}
        </div>
      )}

      <div className="mb-4">
        <label className="mb-1 block text-xs font-semibold" htmlFor="kb-ed-title">
          Назва матеріалу <span className="text-destructive">*</span>{" "}
          <span className="font-normal text-muted-foreground">
            (частина чернетки — читачі побачать після публікації)
          </span>
        </label>
        <input
          id="kb-ed-title"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            setDirty(true);
            if (e.target.value.trim() !== "") setTitleError(false);
          }}
          className={cn(
            "w-full rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring",
            titleError && "border-destructive",
          )}
        />
        {titleError && (
          <p role="alert" className="mt-1 text-xs text-destructive">
            Порожня назва — збереження заблоковано.
          </p>
        )}
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-semibold" htmlFor="kb-ed-folder">
            Папка <span className="font-normal text-muted-foreground">(частина чернетки)</span>
          </label>
          <select
            id="kb-ed-folder"
            value={folderId}
            onChange={(e) => {
              setFolderId(e.target.value);
              setDirty(true);
            }}
            className="w-full rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring"
          >
            <option value="">— без папки —</option>
            {(section.data?.folders ?? []).map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
        <KbOwnerPicker
          itemId={id}
          owner={data.owner}
          section={section.data}
          onChanged={() => {
            invalidate();
            void item.refetch();
          }}
        />
      </div>

      {isPage && editor && (
        <>
          <div className="flex flex-wrap gap-0.5 rounded-t-lg border border-b-0 bg-muted/40 p-1.5">
            {tb("Заголовок H2", <Heading2 className="h-4 w-4" />, () => editor.chain().focus().toggleHeading({ level: 2 }).run(), editor.isActive("heading", { level: 2 }))}
            {tb("Підзаголовок H3", <Heading3 className="h-4 w-4" />, () => editor.chain().focus().toggleHeading({ level: 3 }).run(), editor.isActive("heading", { level: 3 }))}
            <span className="mx-1 w-px bg-border" aria-hidden />
            {tb("Жирний", <Bold className="h-4 w-4" />, () => editor.chain().focus().toggleBold().run(), editor.isActive("bold"))}
            {tb("Курсив", <Italic className="h-4 w-4" />, () => editor.chain().focus().toggleItalic().run(), editor.isActive("italic"))}
            <span className="mx-1 w-px bg-border" aria-hidden />
            {tb("Маркований список", <List className="h-4 w-4" />, () => editor.chain().focus().toggleBulletList().run(), editor.isActive("bulletList"))}
            {tb("Нумерований список", <ListOrdered className="h-4 w-4" />, () => editor.chain().focus().toggleOrderedList().run(), editor.isActive("orderedList"))}
            <span className="mx-1 w-px bg-border" aria-hidden />
            {tb("Посилання", <Link2 className="h-4 w-4" />, setLink, editor.isActive("link"))}
            {tb("Зображення", <ImageIcon className="h-4 w-4" />, () => imageInputRef.current?.click(), false)}
          </div>
          <div className="rounded-b-lg border bg-card">
            <EditorContent editor={editor} />
          </div>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/jpeg,image/png"
            className="hidden"
            onChange={(e) => {
              onPickImage(e.target.files?.[0] ?? null);
              e.target.value = "";
            }}
          />
        </>
      )}

      {closeAsk && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Незбережені зміни"
        >
          <div className="w-full max-w-sm rounded-xl border bg-card p-5 shadow-2xl">
            <div className="text-sm font-semibold">Є незбережені зміни</div>
            <div className="mt-4 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => {
                  setCloseAsk(false);
                  void doSave().then((r) => {
                    if (r !== null) navigate(`/knowledge/items/${id}`);
                  });
                }}
                className="rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground"
              >
                Зберегти й закрити
              </button>
              <button
                type="button"
                onClick={() => navigate(`/knowledge/items/${id}`)}
                className="rounded-md border px-3.5 py-2 text-sm font-medium hover:bg-accent"
              >
                Закрити без збереження
              </button>
              <button
                type="button"
                onClick={() => setCloseAsk(false)}
                className="rounded-md px-3.5 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
              >
                Залишитись
              </button>
            </div>
          </div>
        </div>
      )}
    </PageWrap>
  );
}

function PageWrap({ children }: { children: React.ReactNode }) {
  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto max-w-[900px]">{children}</div>
    </div>
  );
}

function formatTime(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso.includes("T") ? iso : iso.replace(" ", "T") + "Z");
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("uk-UA", { hour: "2-digit", minute: "2-digit" });
}
